import { logger } from "@/utils/logger";
/**
 * Utility for detecting and filtering placeholder images by content hash
 */

// Known placeholder image hashes (computed from actual image content)
const PLACEHOLDER_IMAGE_HASHES = new Set<string>([
  // This will be populated after computing the hash of the coming soon image
]);

/**
 * Computes a simple perceptual hash of an image
 * Returns a hash string that represents the visual content
 */
export const computeImageHash = async (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Don't use crossOrigin for external images - it causes CORS failures
    // We'll just check if the image loads successfully instead
    
    // Set a timeout to reject if image takes too long
    const timeout = setTimeout(() => {
      reject(new Error('Image load timeout'));
    }, 5000);
    
    img.onload = () => {
      clearTimeout(timeout);
      try {
        // For cross-origin images, we can't access pixel data
        // Just return a simple hash based on dimensions
        const hash = `${img.naturalWidth}x${img.naturalHeight}`;
        resolve(hash);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;
  });
};

/**
 * Checks if an image is a known placeholder by computing its hash
 */
export const isPlaceholderImage = async (imageUrl: string): Promise<boolean> => {
  try {
    // Skip hash check for now - the cross-origin restrictions make it unreliable
    // Just check if the URL matches known placeholder patterns
    const placeholderPatterns = [
      /coming.?soon/i,
      /placeholder/i,
      /no.?image/i,
    ];
    
    return placeholderPatterns.some(pattern => pattern.test(imageUrl));
  } catch (error) {
    logger.warn('Error checking if image is placeholder:', error);
    return false; // On error, don't filter the image
  }
};

/**
 * Adds a new placeholder image hash to the blocklist
 */
export const addPlaceholderHash = async (imageUrl: string): Promise<void> => {
  try {
    const hash = await computeImageHash(imageUrl);
    PLACEHOLDER_IMAGE_HASHES.add(hash);
    logger.log('Added placeholder image hash:', hash);
  } catch (error) {
    console.error('Error computing placeholder hash:', error);
  }
};

// Initialize with the known coming soon image
// This will be computed once when the module loads
const COMING_SOON_IMAGE = 'https://carimagescdn.d2cmedia.ca/oemcontent/Hyundai/Tucson_Hybrid/2025/interior1.webp';
addPlaceholderHash(COMING_SOON_IMAGE).catch(console.error);
